import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SkuCombobox from "@/components/SkuCombobox";
import { INDIAN_STATES } from "@/lib/india";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const newItem = () => ({
  uid: `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sku_id: "", quantity: 1, selling_price: "",
});

export default function PlaceOrder() {
  const navigate = useNavigate();
  const [skus, setSkus] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", phone: "", address: "", city: "", state: "", pincode: "", email: "",
    order_type: "COD", order_type_tag: "", amount_collected: 0, notes: "",
    other_charges_amount: 0, other_charges_note: "",
    discount_percentage: 0,
    payment_screenshot: null,
  });
  const [items, setItems] = useState(() => [newItem()]);
  const [maxDiscount, setMaxDiscount] = useState(0);

  const loadSkus = useCallback(async () => {
    try {
      const { data } = await api.get("/skus", { params: { active_only: true } });
      setSkus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setMaxDiscount(Number(data?.max_discount_percentage || 0));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadOrderTypes = useCallback(async () => {
    try {
      const { data } = await api.get("/order-types");
      setOrderTypes(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadSkus(); loadSettings(); loadOrderTypes(); }, [loadSkus, loadSettings, loadOrderTypes]);

  const updateItem = (i, field, value) => {
    const next = [...items];
    const sku = skus.find((s) => s.id === (field === "sku_id" ? value : next[i].sku_id));
    next[i] = { ...next[i], [field]: value };
    if (field === "sku_id") {
      if (sku?.is_free) {
        // Free SKU: lock price=0 and qty=1
        next[i].selling_price = 0;
        next[i].quantity = 1;
      } else {
        next[i].selling_price = "";
      }
    }
    setItems(next);
  };
  const addItem = () => setItems([...items, newItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const lineTotal = (it) => Number(it.quantity || 0) * Number(it.selling_price || 0);
  // Paid subtotal (excludes free SKUs) — used to validate free-product eligibility
  const paidSubtotal = items.reduce((s, it) => {
    const sku = skus.find((x) => x.id === it.sku_id);
    if (sku?.is_free) return s;
    return s + lineTotal(it);
  }, 0);
  const itemsTotal = items.reduce((s, it) => s + lineTotal(it), 0);
  const otherChargesAmt = Number(form.other_charges_amount || 0);
  const subtotal = itemsTotal + otherChargesAmt;
  const discountPct = Math.min(Number(form.discount_percentage || 0), maxDiscount);
  const discountAmt = Math.round((subtotal * discountPct) / 100 * 100) / 100;
  const grandTotal = Math.round((subtotal - discountAmt) * 100) / 100;

  let amountToCollect = 0;
  if (form.order_type === "COD") amountToCollect = grandTotal;
  else if (form.order_type === "Partial COD") amountToCollect = Math.max(grandTotal - Number(form.amount_collected || 0), 0);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, payment_screenshot: data.path });
      toast.success("Screenshot uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.order_type_tag) { toast.error("Select an Order Type"); return; }
    // validation
    for (const it of items) {
      if (!it.sku_id) { toast.error("Select SKU for every line"); return; }
      const sku = skus.find((s) => s.id === it.sku_id);
      if (sku?.is_free) {
        const minAmt = Number(sku.free_min_order_amount || 0);
        if (paidSubtotal < minAmt) {
          toast.error(`Free product '${sku.sku_name}' needs paid order ≥ ₹${minAmt}`);
          return;
        }
        continue;
      }
      if (it.selling_price === "" || it.selling_price === null) {
        toast.error("Enter unit price for every line"); return;
      }
      if (sku && Number(it.selling_price) < Number(sku.minimum_selling_price)) {
        toast.error(`${sku.sku_name}: price is below the minimum allowed`); return;
      }
    }
    setSubmitting(true);
    try {
      if (Number(form.discount_percentage || 0) > maxDiscount) {
        toast.error(`Discount cannot exceed ${maxDiscount}%`);
        return;
      }
      const payload = {
        ...form,
        amount_collected: Number(form.amount_collected || 0),
        other_charges_amount: Number(form.other_charges_amount || 0),
        discount_percentage: Number(form.discount_percentage || 0),
        items: items.map((it) => ({
          sku_id: it.sku_id, quantity: Number(it.quantity), selling_price: Number(it.selling_price),
        })),
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_id} created`);
      navigate("/orders");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Place Manual Order</h1>
        <p className="text-sm text-muted-foreground">Enter customer and product details</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card className="p-4 md:p-6 space-y-4">
          <h2 className="font-display font-semibold text-lg">Customer details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Customer name</Label>
              <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="cust-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Phone number</Label>
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone-input" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Full address</Label>
              <Textarea required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-address-input" />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="cust-city-input" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="cust-state-input"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {INDIAN_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pincode</Label>
              <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="cust-pincode-input" />
            </div>
            <div className="space-y-2">
              <Label>Email (optional)</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email-input" />
            </div>
          </div>
        </Card>

        <Card className="p-4 md:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-lg">Products</h2>
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Add item
            </Button>
          </div>
          <div className="space-y-3">
            {items.map((it, i) => {
              const sku = skus.find((s) => s.id === it.sku_id);
              return (
                <div key={it.uid} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border-b border-border pb-3 last:border-0" data-testid={`order-item-${i}`}>
                  <div className="md:col-span-5 space-y-2">
                    <Label>SKU</Label>
                    <SkuCombobox
                      skus={skus}
                      value={it.sku_id}
                      onChange={(v) => updateItem(i, "sku_id", v)}
                      testid={`item-sku-${i}`}
                      getLabel={(s) =>
                        s.is_free
                          ? `${s.sku_name} · FREE if order ≥ ₹${Number(s.free_min_order_amount || 0)}`
                          : s.sku_name
                      }
                    />
                    {sku?.is_free && paidSubtotal < Number(sku.free_min_order_amount || 0) && (
                      <div className="text-xs text-amber-700" data-testid={`item-free-warning-${i}`}>
                        Add ₹{(Number(sku.free_min_order_amount || 0) - paidSubtotal).toFixed(2)} more in paid items to qualify for this free product.
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      max={sku?.is_free ? 1 : undefined}
                      value={it.quantity}
                      disabled={sku?.is_free}
                      onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      data-testid={`item-qty-${i}`}
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>Unit price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder={sku?.is_free ? "₹0 (free)" : "Enter price"}
                      value={sku?.is_free ? 0 : it.selling_price}
                      disabled={sku?.is_free}
                      onChange={(e) => updateItem(i, "selling_price", e.target.value)}
                      data-testid={`item-price-${i}`}
                    />
                    {sku && !sku.is_free && it.selling_price !== "" && Number(it.selling_price) < Number(sku.minimum_selling_price) && (
                      <div className="text-xs text-red-600" data-testid={`item-price-error-${i}`}>
                        Price is below the minimum allowed for this SKU
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>Line total</Label>
                    <div className="text-sm font-medium" data-testid={`item-total-${i}`}>₹{lineTotal(it).toFixed(2)}</div>
                  </div>
                  <div className="md:col-span-1">
                    {items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} data-testid={`remove-item-${i}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end pt-2">
            <div className="text-right space-y-0.5">
              <div className="text-xs text-muted-foreground">Items total: ₹{itemsTotal.toFixed(2)}</div>
              {otherChargesAmt > 0 && (
                <div className="text-xs text-muted-foreground">
                  Other charges{form.other_charges_note ? ` (${form.other_charges_note})` : ""}: ₹{otherChargesAmt.toFixed(2)}
                </div>
              )}
              {discountAmt > 0 && (
                <div className="text-xs text-green-700">
                  Discount ({discountPct}%): −₹{discountAmt.toFixed(2)}
                </div>
              )}
              <div className="text-xs text-muted-foreground uppercase pt-1">Order total</div>
              <div className="font-display font-bold text-2xl" data-testid="order-grand-total">₹{grandTotal.toFixed(2)}</div>
            </div>
          </div>
        </Card>

        <Card className="p-4 md:p-6 space-y-4">
          <h2 className="font-display font-semibold text-lg">Other charges</h2>
          <p className="text-sm text-muted-foreground -mt-2">Add any extra charge (shipping, packaging, COD fee, etc.). It will be included in the order total.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Charge amount (₹)</Label>
              <Input
                type="number" step="0.01" min="0"
                placeholder="0"
                value={form.other_charges_amount}
                onChange={(e) => setForm({ ...form, other_charges_amount: e.target.value })}
                data-testid="other-charges-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Charge description</Label>
              <Input
                placeholder="e.g. COD handling, packaging"
                value={form.other_charges_note}
                onChange={(e) => setForm({ ...form, other_charges_note: e.target.value })}
                data-testid="other-charges-note-input"
              />
            </div>
          </div>
        </Card>

        <Card className="p-4 md:p-6 space-y-4">
          <h2 className="font-display font-semibold text-lg">Discount</h2>
          <p className="text-sm text-muted-foreground -mt-2">
            {maxDiscount > 0
              ? `Maximum allowed: ${maxDiscount}% (set by admin)`
              : "Discounts are currently disabled by the admin."}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Discount percentage (%)</Label>
              <Input
                type="number" step="0.01" min="0" max={maxDiscount}
                disabled={maxDiscount === 0}
                placeholder="0"
                value={form.discount_percentage}
                onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                data-testid="discount-input"
              />
              {Number(form.discount_percentage || 0) > maxDiscount && (
                <div className="text-xs text-red-600">Maximum allowed is {maxDiscount}%</div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Discount amount</Label>
              <div className="text-sm font-medium pt-2" data-testid="discount-amount">₹{discountAmt.toFixed(2)}</div>
            </div>
          </div>
        </Card>

        <Card className="p-4 md:p-6 space-y-4">
          <h2 className="font-display font-semibold text-lg">Payment & notes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Order Type *</Label>
              <Select
                value={form.order_type_tag}
                onValueChange={(v) => setForm({ ...form, order_type_tag: v })}
              >
                <SelectTrigger data-testid="order-type-tag-select">
                  <SelectValue placeholder={orderTypes.length ? "Select order type..." : "No order types configured — ask admin"} />
                </SelectTrigger>
                <SelectContent>
                  {orderTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Type</Label>
              <Select
                value={form.order_type}
                onValueChange={(v) => setForm({ ...form, order_type: v, amount_collected: v === "Partial COD" ? form.amount_collected : 0 })}
              >
                <SelectTrigger data-testid="order-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COD">COD</SelectItem>
                  <SelectItem value="Prepaid">Prepaid</SelectItem>
                  <SelectItem value="Partial COD">Partial COD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.order_type === "Partial COD" && (
              <div className="space-y-2">
                <Label>Amount already collected (₹)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.amount_collected}
                  onChange={(e) => setForm({ ...form, amount_collected: e.target.value })}
                  data-testid="amount-collected-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Amount to collect (₹)</Label>
              <Input
                type="number"
                value={amountToCollect.toFixed(2)}
                disabled
                data-testid="amount-collect-input"
              />
              <div className="text-xs text-muted-foreground">
                {form.order_type === "Prepaid" && "Already paid in full"}
                {form.order_type === "COD" && "Full amount to be collected on delivery"}
                {form.order_type === "Partial COD" && "Remaining balance after part-payment"}
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Payment screenshot (optional)</Label>
              <div className="flex items-center gap-3">
                <Input type="file" accept="image/*" onChange={onUpload} data-testid="screenshot-input" />
                {form.payment_screenshot && <span className="text-xs text-green-700">Uploaded</span>}
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="notes-input" />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={submitting} data-testid="submit-order-btn">
            {submitting ? "Submitting..." : "Submit Order"}
          </Button>
        </div>
      </form>
    </div>
  );
}
